export type MaintenanceStatus = 'active' | 'scheduled' | 'completed' | 'cancelled';

export type MaintenanceType = 'global' | 'per_module';

export type MaintenanceModule =
  | 'landing'
  | 'admin'
  | 'super_admin'
  | 'apps'
  | 'tv'
  | 'api'
  | 'all';

export interface MaintenanceEntry {
  id: string;
  type: MaintenanceType;
  status: MaintenanceStatus;
  name: string;
  description: string;
  modules: MaintenanceModule[];
  customMessage: string;
  startDate: string;
  endDate: string;
  duration: string;
  allowedIPs: string[];
  allowedUsers: string[];
  allowedRoles: string[];
  customPageEnabled: boolean;
  customPageTheme: string;
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
  completedBy: string | null;
  notes: string;
  affectedServices: string[];
  rollbackPlan: string;
}

export interface MaintenanceSchedule {
  id: string;
  name: string;
  type: MaintenanceType;
  modules: MaintenanceModule[];
  scheduledDate: string;
  estimatedDuration: string;
  customMessage: string;
  allowedIPs: string[];
  allowedRoles: string[];
  createdBy: string;
  createdAt: string;
  enabled: boolean;
  recurring: boolean;
  recurringCron: string | null;
  description: string;
  affectedServices: string[];
}

export const statusMeta: Record<MaintenanceStatus, { label: string; color: string; bg: string; dot: string; icon: string }> = {
  active: {
    label: 'Activo',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    dot: 'bg-amber-500',
    icon: 'ri-tools-line',
  },
  scheduled: {
    label: 'Programado',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    dot: 'bg-sky-500',
    icon: 'ri-calendar-event-line',
  },
  completed: {
    label: 'Completado',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    dot: 'bg-emerald-500',
    icon: 'ri-checkbox-circle-line',
  },
  cancelled: {
    label: 'Cancelado',
    color: 'text-foreground-500',
    bg: 'bg-foreground-100',
    dot: 'bg-foreground-400',
    icon: 'ri-close-circle-line',
  },
};

export const moduleMeta: Record<MaintenanceModule, { label: string; icon: string; color: string }> = {
  all: { label: 'Todos los módulos', icon: 'ri-global-line', color: 'text-foreground-600' },
  landing: { label: 'Landing', icon: 'ri-earth-line', color: 'text-emerald-500' },
  admin: { label: 'Admin', icon: 'ri-dashboard-line', color: 'text-sky-500' },
  super_admin: { label: 'Super Admin', icon: 'ri-shield-line', color: 'text-violet-500' },
  apps: { label: 'Apps', icon: 'ri-smartphone-line', color: 'text-orange-500' },
  tv: { label: 'TV', icon: 'ri-tv-line', color: 'text-rose-500' },
  api: { label: 'API', icon: 'ri-terminal-box-line', color: 'text-cyan-500' },
};

export const allModules: MaintenanceModule[] = ['landing', 'admin', 'super_admin', 'apps', 'tv', 'api'];

export const allowedRolesOptions = [
  'Super Admin',
  'Administrador',
  'Soporte Técnico',
  'Desarrollador',
  'DevOps',
  'QA',
  'Owner',
];

export const globalIPs = [
  '192.168.100.1',
  '192.168.100.2',
  '192.168.100.3',
  '10.0.0.1',
  '10.0.0.2',
  '10.0.0.3',
];

export const maintenanceEntries: MaintenanceEntry[] = [
  {
    id: 'MNT-001',
    type: 'global',
    status: 'active',
    name: 'Mantenimiento General - Actualización Infraestructura',
    description: 'Actualización completa de la infraestructura de servidores. Migración de balanceadores de carga y optimización de bases de datos.',
    modules: ['all'],
    customMessage: 'Estamos realizando tareas de mantenimiento para mejorar tu experiencia. Volveremos en aproximadamente 2 horas. ¡Gracias por tu paciencia!',
    startDate: '2026-07-12T06:00:00Z',
    endDate: '2026-07-12T08:00:00Z',
    duration: '2 horas',
    allowedIPs: ['192.168.100.1', '192.168.100.2', '10.0.0.1'],
    allowedUsers: ['carlos.mendoza@readdy.ai', 'diego.vargas@readdy.ai'],
    allowedRoles: ['Super Admin', 'DevOps'],
    customPageEnabled: true,
    customPageTheme: 'dark',
    createdBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    createdAt: '2026-07-11T18:00:00Z',
    completedAt: null,
    completedBy: null,
    notes: 'Ventana de mantenimiento aprobada por dirección. Se notificó a todos los usuarios con 12 horas de anticipación. Rollback plan listo en caso de fallo.',
    affectedServices: ['Supabase', 'API REST', 'Streaming', 'CDN', 'Storage'],
    rollbackPlan: 'Restaurar snapshots de load balancers. Rollback de migración de base de datos con backup pre-deploy BCK-006.',
  },
  {
    id: 'MNT-002',
    type: 'per_module',
    status: 'active',
    name: 'Actualización Panel Admin - v4.3.0',
    description: 'Despliegue de nueva versión del panel de administración con mejoras en el dashboard y nuevos reportes.',
    modules: ['admin'],
    customMessage: 'El panel de administración está en mantenimiento. La radio y la app móvil siguen funcionando normalmente.',
    startDate: '2026-07-12T09:00:00Z',
    endDate: '2026-07-12T09:30:00Z',
    duration: '30 minutos',
    allowedIPs: ['192.168.100.1', '192.168.100.2', '192.168.100.3'],
    allowedUsers: ['carlos.mendoza@readdy.ai', 'laura.ramirez@readdy.ai'],
    allowedRoles: ['Super Admin', 'Administrador'],
    customPageEnabled: true,
    customPageTheme: 'light',
    createdBy: 'Laura Ramírez (laura.ramirez@readdy.ai)',
    createdAt: '2026-07-12T08:30:00Z',
    completedAt: null,
    completedBy: null,
    notes: 'Solo afecta al panel admin. Las radios, apps y API siguen operativas. Hotfix aplicado con rollback automático si falla.',
    affectedServices: ['Admin Panel'],
    rollbackPlan: 'Deploy automático de versión anterior v4.2.9. Backup pre-deploy MNT-002-BCK ya generado.',
  },
  {
    id: 'MNT-003',
    type: 'global',
    status: 'scheduled',
    name: 'Migración de Base de Datos - PostgreSQL 16',
    description: 'Migración planificada de PostgreSQL 15 a PostgreSQL 16 con nuevas funcionalidades de particionamiento y rendimiento mejorado.',
    modules: ['all'],
    customMessage: 'Mantenimiento programado: Estaremos actualizando nuestros sistemas el domingo. La plataforma no estará disponible de 02:00 a 06:00 UTC.',
    startDate: '2026-07-14T02:00:00Z',
    endDate: '2026-07-14T06:00:00Z',
    duration: '4 horas',
    allowedIPs: ['192.168.100.1', '10.0.0.1', '10.0.0.2'],
    allowedUsers: ['diego.vargas@readdy.ai', 'ana.torres@readdy.ai'],
    allowedRoles: ['Super Admin', 'DevOps', 'Desarrollador'],
    customPageEnabled: true,
    customPageTheme: 'dark',
    createdBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    createdAt: '2026-07-10T14:00:00Z',
    completedAt: null,
    completedBy: null,
    notes: 'Ventana de baja audiencia seleccionada. Se realizará backup completo antes de la migración. Equipo completo de DevOps estará en guardia.',
    affectedServices: ['Supabase', 'API REST', 'Streaming', 'Storage', 'CDN'],
    rollbackPlan: 'Restauración completa desde backup pre-migración BCK-XXX. Rollback estimado en 45 minutos.',
  },
  {
    id: 'MNT-004',
    type: 'per_module',
    status: 'scheduled',
    name: 'Actualización API - Rate Limiting v2',
    description: 'Implementación de nuevo sistema de rate limiting con Redis. Mejora en la distribución de tráfico y protección contra abusos.',
    modules: ['api'],
    customMessage: 'La API estará en mantenimiento breve. Las aplicaciones cliente pueden experimentar interrupciones momentáneas.',
    startDate: '2026-07-13T03:00:00Z',
    endDate: '2026-07-13T03:15:00Z',
    duration: '15 minutos',
    allowedIPs: ['10.0.0.1', '10.0.0.2'],
    allowedUsers: ['diego.vargas@readdy.ai'],
    allowedRoles: ['DevOps', 'Desarrollador'],
    customPageEnabled: true,
    customPageTheme: 'light',
    createdBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    createdAt: '2026-07-11T10:00:00Z',
    completedAt: null,
    completedBy: null,
    notes: 'Ventana mínima. Se usa blue-green deployment para minimizar downtime. Reintentos automáticos configurados en clientes.',
    affectedServices: ['API REST'],
    rollbackPlan: 'Switch inmediato a entorno blue. Rollback menor a 30 segundos.',
  },
  {
    id: 'MNT-005',
    type: 'per_module',
    status: 'scheduled',
    name: 'Mantenimiento CDN - Purga de Cache Global',
    description: 'Purga completa del cache en todos los nodos CDN de Cloudflare. Actualización de configuraciones de caché para assets estáticos.',
    modules: ['landing', 'apps', 'tv'],
    customMessage: 'Estamos optimizando la entrega de contenido. Algunas imágenes pueden tardar unos segundos en cargar.',
    startDate: '2026-07-13T01:00:00Z',
    endDate: '2026-07-13T01:30:00Z',
    duration: '30 minutos',
    allowedIPs: ['192.168.100.1'],
    allowedUsers: ['carlos.mendoza@readdy.ai', 'diego.vargas@readdy.ai'],
    allowedRoles: ['Super Admin', 'DevOps'],
    customPageEnabled: false,
    customPageTheme: 'light',
    createdBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    createdAt: '2026-07-11T16:00:00Z',
    completedAt: null,
    completedBy: null,
    notes: 'Purga de cache programada en horario de baja carga. Los assets se regenerarán bajo demanda. Sin impacto en funcionalidad core.',
    affectedServices: ['CDN', 'Cloudflare'],
    rollbackPlan: 'Restaurar configuración de caché anterior desde backup de Cloudflare. Tiempo de rollback: 5 minutos.',
  },
  {
    id: 'MNT-006',
    type: 'global',
    status: 'completed',
    name: 'Actualización de Seguridad - Parche Crítico',
    description: 'Aplicación de parche de seguridad crítico CVE-2026-7841. Vulnerabilidad en el sistema de autenticación que requería actualización inmediata.',
    modules: ['all'],
    customMessage: 'Mantenimiento de seguridad completado. Todos los sistemas operando normalmente.',
    startDate: '2026-07-11T02:00:00Z',
    endDate: '2026-07-11T02:45:00Z',
    duration: '45 minutos',
    allowedIPs: ['192.168.100.1', '192.168.100.2'],
    allowedUsers: ['diego.vargas@readdy.ai'],
    allowedRoles: ['Super Admin', 'DevOps'],
    customPageEnabled: true,
    customPageTheme: 'dark',
    createdBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    createdAt: '2026-07-11T00:30:00Z',
    completedAt: '2026-07-11T02:43:00Z',
    completedBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    notes: 'Parche aplicado exitosamente en 43 minutos (2 minutos antes del tiempo estimado). Verificación de seguridad post-deploy completada sin incidentes.',
    affectedServices: ['Supabase', 'API REST', 'Streaming', 'Storage'],
    rollbackPlan: 'Hotfix revert aplicado automáticamente en caso de fallo. No fue necesario.',
  },
  {
    id: 'MNT-007',
    type: 'per_module',
    status: 'completed',
    name: 'Actualización App Móvil - iOS & Android v5.2',
    description: 'Actualización de backend para soportar nuevas funcionalidades de la app móvil. Sincronización offline y notificaciones push mejoradas.',
    modules: ['apps'],
    customMessage: 'Actualización de app completada. Descarga la nueva versión desde tu tienda de aplicaciones.',
    startDate: '2026-07-10T14:00:00Z',
    endDate: '2026-07-10T14:45:00Z',
    duration: '45 minutos',
    allowedIPs: ['192.168.100.1', '192.168.100.2', '192.168.100.3'],
    allowedUsers: ['laura.ramirez@readdy.ai', 'ana.torres@readdy.ai'],
    allowedRoles: ['Super Admin', 'Administrador', 'Desarrollador'],
    customPageEnabled: true,
    customPageTheme: 'light',
    createdBy: 'Laura Ramírez (laura.ramirez@readdy.ai)',
    createdAt: '2026-07-10T10:00:00Z',
    completedAt: '2026-07-10T14:42:00Z',
    completedBy: 'Laura Ramírez (laura.ramirez@readdy.ai)',
    notes: 'Deploy completado. iOS y Android ya reciben la actualización v5.2. Rollout gradual al 100% de usuarios.',
    affectedServices: ['API REST', 'OneSignal', 'Firebase'],
    rollbackPlan: 'Revert de backend y notificación a stores para revertir versión de app.',
  },
  {
    id: 'MNT-008',
    type: 'per_module',
    status: 'completed',
    name: 'Optimización Storage - Migración S3 a R2',
    description: 'Migración de almacenamiento de archivos desde AWS S3 a Cloudflare R2 para reducir latencia y costos de egress.',
    modules: ['admin', 'super_admin'],
    customMessage: 'Migración de almacenamiento completada. Todos los archivos transferidos exitosamente.',
    startDate: '2026-07-09T22:00:00Z',
    endDate: '2026-07-10T01:30:00Z',
    duration: '3 horas 30 minutos',
    allowedIPs: ['192.168.100.1'],
    allowedUsers: ['carlos.mendoza@readdy.ai', 'diego.vargas@readdy.ai'],
    allowedRoles: ['Super Admin', 'DevOps'],
    customPageEnabled: false,
    customPageTheme: 'light',
    createdBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    createdAt: '2026-07-09T16:00:00Z',
    completedAt: '2026-07-10T01:25:00Z',
    completedBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    notes: 'Migración completada con 5 minutos de anticipación. 2.3 TB transferidos sin pérdida de datos. Verificación de integridad SHA256 completada.',
    affectedServices: ['Storage', 'CDN'],
    rollbackPlan: 'Mantener bucket S3 original por 30 días como respaldo. Rollback implica redireccionar DNS.',
  },
  {
    id: 'MNT-009',
    type: 'per_module',
    status: 'completed',
    name: 'Actualización TV App - Chromecast Support',
    description: 'Implementación de soporte para Chromecast en la aplicación de TV. Nuevo reproductor con controles mejorados.',
    modules: ['tv'],
    customMessage: 'TV App actualizada con soporte Chromecast. Disfruta del contenido en tu pantalla grande.',
    startDate: '2026-07-08T08:00:00Z',
    endDate: '2026-07-08T09:00:00Z',
    duration: '1 hora',
    allowedIPs: ['192.168.100.1', '192.168.100.2'],
    allowedUsers: ['laura.ramirez@readdy.ai'],
    allowedRoles: ['Super Admin', 'Desarrollador'],
    customPageEnabled: true,
    customPageTheme: 'dark',
    createdBy: 'Laura Ramírez (laura.ramirez@readdy.ai)',
    createdAt: '2026-07-08T07:00:00Z',
    completedAt: '2026-07-08T08:52:00Z',
    completedBy: 'Laura Ramírez (laura.ramirez@readdy.ai)',
    notes: 'Soporte Chromecast implementado. Testing en 5 dispositivos diferentes completado exitosamente.',
    affectedServices: ['API REST', 'Streaming', 'CDN'],
    rollbackPlan: 'Feature flag para deshabilitar Chromecast sin afectar reproducción normal.',
  },
  {
    id: 'MNT-010',
    type: 'global',
    status: 'completed',
    name: 'Balanceo de Carga - Nuevo Nodo sa-east-1',
    description: 'Adición de nuevo nodo en São Paulo (sa-east-1) para mejorar latencia en Latinoamérica. Configuración de balanceadores.',
    modules: ['all'],
    customMessage: 'Nuevo servidor en Latinoamérica activado. Mejor velocidad para usuarios de la región.',
    startDate: '2026-07-07T04:00:00Z',
    endDate: '2026-07-07T06:30:00Z',
    duration: '2 horas 30 minutos',
    allowedIPs: ['192.168.100.1', '10.0.0.1'],
    allowedUsers: ['diego.vargas@readdy.ai', 'carlos.mendoza@readdy.ai'],
    allowedRoles: ['Super Admin', 'DevOps'],
    customPageEnabled: true,
    customPageTheme: 'dark',
    createdBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    createdAt: '2026-07-06T18:00:00Z',
    completedAt: '2026-07-07T06:15:00Z',
    completedBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    notes: 'Nodo sa-east-1 operativo. Latencia reducida en 45% para usuarios de LATAM. Monitoreo 24h post-deploy sin incidentes.',
    affectedServices: ['Supabase', 'API REST', 'Streaming', 'CDN', 'Storage'],
    rollbackPlan: 'Remover nodo del balanceador. Tráfico redirigido a us-east-1 y us-west-2.',
  },
  {
    id: 'MNT-011',
    type: 'per_module',
    status: 'cancelled',
    name: 'Rediseño Landing Page - Dark Mode',
    description: 'Implementación de modo oscuro en la landing page. Rediseño completo con nuevo sistema de temas. CANCELADO por cambio de prioridades.',
    modules: ['landing'],
    customMessage: '—',
    startDate: '2026-07-10T10:00:00Z',
    endDate: '2026-07-10T12:00:00Z',
    duration: '2 horas',
    allowedIPs: [],
    allowedUsers: [],
    allowedRoles: [],
    customPageEnabled: false,
    customPageTheme: 'light',
    createdBy: 'Ana Torres (ana.torres@readdy.ai)',
    createdAt: '2026-07-09T14:00:00Z',
    completedAt: null,
    completedBy: null,
    notes: 'Cancelado por dirección. Prioridad reasignada a integración de pagos Stripe. Se retomará en Q4 2026.',
    affectedServices: ['CDN'],
    rollbackPlan: '—',
  },
  {
    id: 'MNT-012',
    type: 'per_module',
    status: 'cancelled',
    name: 'Integración Analytics - Google Analytics 5',
    description: 'Actualización a Google Analytics 5 en todos los módulos. CANCELADO por incompatibilidad detectada con el sistema de métricas interno.',
    modules: ['landing', 'apps', 'api'],
    customMessage: '—',
    startDate: '2026-07-06T10:00:00Z',
    endDate: '2026-07-06T13:00:00Z',
    duration: '3 horas',
    allowedIPs: [],
    allowedUsers: [],
    allowedRoles: [],
    customPageEnabled: false,
    customPageTheme: 'light',
    createdBy: 'Ana Torres (ana.torres@readdy.ai)',
    createdAt: '2026-07-05T15:00:00Z',
    completedAt: null,
    completedBy: null,
    notes: 'Cancelado por incompatibilidad entre GA5 y el sistema de métricas propietario. Se evaluará alternativa con Mixpanel.',
    affectedServices: ['CDN'],
    rollbackPlan: '—',
  },
];

export const maintenanceSchedules: MaintenanceSchedule[] = [
  {
    id: 'SCH-MNT-001',
    name: 'Mantenimiento Mensual - Parcheo de Seguridad',
    type: 'global',
    modules: ['all'],
    scheduledDate: '2026-08-01T02:00:00Z',
    estimatedDuration: '2 horas',
    customMessage: 'Mantenimiento mensual programado. La plataforma no estará disponible de 02:00 a 04:00 UTC.',
    allowedIPs: ['192.168.100.1', '192.168.100.2'],
    allowedRoles: ['Super Admin', 'DevOps'],
    createdBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    createdAt: '2026-07-01T10:00:00Z',
    enabled: true,
    recurring: true,
    recurringCron: '0 2 1 * *',
    description: 'Mantenimiento mensual recurrente para aplicar parches de seguridad del sistema operativo y dependencias. Primer día de cada mes.',
    affectedServices: ['Supabase', 'API REST', 'Streaming', 'CDN', 'Storage'],
  },
  {
    id: 'SCH-MNT-002',
    name: 'Backup y Verificación Trimestral',
    type: 'global',
    modules: ['all'],
    scheduledDate: '2026-09-15T03:00:00Z',
    estimatedDuration: '3 horas',
    customMessage: 'Verificación trimestral de sistemas. Posibles interrupciones breves.',
    allowedIPs: ['192.168.100.1', '10.0.0.1'],
    allowedRoles: ['Super Admin', 'DevOps', 'QA'],
    createdBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    createdAt: '2026-07-05T08:00:00Z',
    enabled: true,
    recurring: true,
    recurringCron: '0 3 15 */3 *',
    description: 'Verificación trimestral de backups, pruebas de restauración y validación de integridad de datos.',
    affectedServices: ['Supabase', 'Storage', 'API REST'],
  },
  {
    id: 'SCH-MNT-003',
    name: 'Actualización CDN - Reglas de Cache',
    type: 'per_module',
    modules: ['landing', 'apps'],
    scheduledDate: '2026-07-20T01:00:00Z',
    estimatedDuration: '45 minutos',
    customMessage: 'Optimización de entrega de contenido en progreso.',
    allowedIPs: ['192.168.100.1'],
    allowedRoles: ['DevOps'],
    createdBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    createdAt: '2026-07-10T14:00:00Z',
    enabled: true,
    recurring: false,
    recurringCron: null,
    description: 'Actualización única de reglas de caché para optimizar entrega de assets estáticos.',
    affectedServices: ['CDN', 'Cloudflare'],
  },
  {
    id: 'SCH-MNT-004',
    name: 'Rotación de Certificados SSL',
    type: 'global',
    modules: ['all'],
    scheduledDate: '2026-07-25T02:00:00Z',
    estimatedDuration: '30 minutos',
    customMessage: 'Renovación de certificados de seguridad en progreso.',
    allowedIPs: ['192.168.100.1', '10.0.0.1'],
    allowedRoles: ['Super Admin', 'DevOps'],
    createdBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    createdAt: '2026-07-08T12:00:00Z',
    enabled: true,
    recurring: true,
    recurringCron: '0 2 25 */3 *',
    description: 'Rotación trimestral de certificados SSL/TLS. Renovación automática con LetsEncrypt.',
    affectedServices: ['API REST', 'CDN', 'Cloudflare'],
  },
  {
    id: 'SCH-MNT-005',
    name: 'Prueba de Recuperación ante Desastres',
    type: 'global',
    modules: ['all'],
    scheduledDate: '2026-10-01T00:00:00Z',
    estimatedDuration: '6 horas',
    customMessage: 'Simulacro de recuperación ante desastres. La plataforma completa estará en modo mantenimiento.',
    allowedIPs: ['192.168.100.1', '192.168.100.2', '192.168.100.3'],
    allowedRoles: ['Super Admin', 'DevOps', 'QA'],
    createdBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    createdAt: '2026-06-15T09:00:00Z',
    enabled: true,
    recurring: true,
    recurringCron: '0 0 1 */6 *',
    description: 'Simulacro semestral de disaster recovery. Prueba completa de restauración desde backups y failover.',
    affectedServices: ['Supabase', 'API REST', 'Streaming', 'SMTP', 'Storage', 'CDN', 'Cloudflare'],
  },
  {
    id: 'SCH-MNT-006',
    name: 'Actualización App Móvil - Backend v5.3',
    type: 'per_module',
    modules: ['apps', 'api'],
    scheduledDate: '2026-07-18T14:00:00Z',
    estimatedDuration: '1 hora',
    customMessage: 'Preparando nuevas funcionalidades para la app móvil.',
    allowedIPs: ['192.168.100.1', '192.168.100.2'],
    allowedRoles: ['Super Admin', 'Desarrollador'],
    createdBy: 'Laura Ramírez (laura.ramirez@readdy.ai)',
    createdAt: '2026-07-10T11:00:00Z',
    enabled: true,
    recurring: false,
    recurringCron: null,
    description: 'Actualización de backend para soporte de nuevas funcionalidades en app móvil v5.3.',
    affectedServices: ['API REST', 'OneSignal', 'Firebase'],
  },
  {
    id: 'SCH-MNT-007',
    name: 'Optimización Base de Datos - Índices',
    type: 'per_module',
    modules: ['admin', 'super_admin', 'api'],
    scheduledDate: '2026-07-22T03:00:00Z',
    estimatedDuration: '1 hora 30 minutos',
    customMessage: 'Optimización de base de datos en progreso. Posible degradación temporal en consultas.',
    allowedIPs: ['10.0.0.1'],
    allowedRoles: ['DevOps'],
    createdBy: 'Diego Vargas (diego.vargas@readdy.ai)',
    createdAt: '2026-07-09T16:00:00Z',
    enabled: true,
    recurring: false,
    recurringCron: null,
    description: 'Creación de nuevos índices para optimizar consultas frecuentes. Análisis de slow queries completado.',
    affectedServices: ['Supabase', 'API REST'],
  },
  {
    id: 'SCH-MNT-008',
    name: 'Mantenimiento Infraestructura - Limpieza Logs',
    type: 'global',
    modules: ['all'],
    scheduledDate: '2026-07-28T04:00:00Z',
    estimatedDuration: '1 hora',
    customMessage: 'Limpieza de logs del sistema en progreso.',
    allowedIPs: ['192.168.100.1'],
    allowedRoles: ['DevOps'],
    createdBy: 'Carlos Mendoza (carlos.mendoza@readdy.ai)',
    createdAt: '2026-07-07T10:00:00Z',
    enabled: false,
    recurring: false,
    recurringCron: null,
    description: 'Limpieza de logs y archivos temporales para liberar espacio en servidores. DESHABILITADO por conflicto con otras tareas.',
    affectedServices: ['Storage'],
  },
];

export const systemStatus = {
  isGlobalMaintenanceActive: true,
  activeGlobalMaintenanceId: 'MNT-001',
  maintenanceSince: '2026-07-12T06:00:00Z',
  estimatedCompletion: '2026-07-12T08:00:00Z',
  activeModuleMaintenance: [
    { module: 'admin', maintenanceId: 'MNT-002', since: '2026-07-12T09:00:00Z' },
  ],
  modulesUnderMaintenance: 2,
  totalModules: 7,
  upcomingSchedules: 8,
  completedThisMonth: 5,
  cancelledThisMonth: 2,
};

export const maintenanceHistory = maintenanceEntries
  .filter((e) => e.status === 'completed' || e.status === 'cancelled')
  .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

export const activeMaintenance = maintenanceEntries.filter((e) => e.status === 'active');
export const scheduledMaintenance = maintenanceEntries.filter((e) => e.status === 'scheduled');